import { ErrorState, Loading, PageHead } from '@ly360/ui';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TaskRow } from '../crm/data';
import { NewTaskDialog } from '../crm/forms';
import { TaskList } from '../crm/TaskList';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

export function TasksPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const [status, setStatus] = useState<'open' | 'done'>('open');
  const [who, setWho] = useState<'me' | 'any'>('me');
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ['tasks', status, who],
    queryFn: () => api<{ items: TaskRow[] }>(`/v1/tasks?status=${status}&assignee=${who}`),
  });
  return (
    <>
      <PageHead
        eyebrow={t('nav.main')}
        title={t('crm.tasks.title')}
        subtitle={t('crm.tasks.subtitle')}
        actions={
          can('tasks:write') && (
            <button className="btn btn-grad" onClick={() => setOpen(true)} data-testid="add-task">
              <Plus size={16} />
              {t('crm.tasks.new')}
            </button>
          )
        }
      />
      <section className="card">
        <div className="toolbar">
          <div className="segmented" role="group" aria-label={t('crm.tasks.title')}>
            {(['open', 'done'] as const).map((s) => (
              <button key={s} aria-pressed={status === s} onClick={() => setStatus(s)}>
                {t(`crm.tasks.filter.${s}`)}
              </button>
            ))}
          </div>
          <div className="segmented" role="group" aria-label={t('crm.tasks.field.assignee')}>
            <button aria-pressed={who === 'me'} onClick={() => setWho('me')}>
              {t('crm.tasks.filter.mine')}
            </button>
            <button aria-pressed={who === 'any'} onClick={() => setWho('any')}>
              {t('crm.tasks.filter.everyone')}
            </button>
          </div>
        </div>
        {q.isLoading && <Loading />}
        {q.isError && <ErrorState error={q.error} />}
        {q.data && <TaskList items={q.data.items} />}
      </section>
      {open && <NewTaskDialog open onClose={() => setOpen(false)} />}
    </>
  );
}
